import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../ui/table';
import { Alert, AlertDescription } from '../ui/alert';
import { Spinner } from '../ui/spinner';
import { Image as ImageIcon } from 'lucide-react';
import { useBrand } from '../../context/BrandContext';
import figmaReviewAPI from '../../services/figmaReviewService';

// The banners the plugin has pushed.
//
// One row per FRAME, because a frame is what a designer selects in Figma and therefore what gets
// translated. The file is a label on the row, not a level of its own: nothing is ever translated
// at file level, and a Figma key carries no frame at all -- the membership behind this list exists
// only because the plugin reports, per string, which frame it was found in.

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  });
}

// One language's standing on a banner. Waiting outranks done: the number a reviewer acts on is how
// much is left, so it is the one that gets the colour.
function LanguageChip({ entry }) {
  if (entry.pending > 0) {
    return (
      <Badge variant="warning" className="uppercase">
        {entry.language} · {entry.pending}
      </Badge>
    );
  }
  return (
    <Badge variant="success" className="uppercase">
      {entry.language}
    </Badge>
  );
}

const FigmaReviewPage = () => {
  const navigate = useNavigate();
  const { activeBrand } = useBrand();
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Identifies the most recent load, so responses that arrive out of order can be dropped.
  const latestLoadID = useRef(0);

  const load = useCallback(async () => {
    // Switching brands faster than a response comes back leaves two loads in flight, and the older
    // one landing last would list another brand's banners. Only the newest may write.
    const requestID = ++latestLoadID.current;
    setLoading(true);
    setError('');
    try {
      const { data } = await figmaReviewAPI.listBanners();
      if (requestID !== latestLoadID.current) return;
      setBanners(data.items || []);
    } catch (err) {
      if (requestID !== latestLoadID.current) return;
      setError(err.response?.data?.error || 'Failed to load banners');
    } finally {
      if (requestID === latestLoadID.current) setLoading(false);
    }
  }, []);

  // Banners are per-brand, so switching brands reloads the list.
  useEffect(() => { load(); }, [load, activeBrand]);

  const waitingOn = (banner) =>
    (banner.languages || []).reduce((sum, entry) => sum + entry.pending, 0);

  return (
    <div className="max-w-[1100px] mx-auto py-6">
      <Card className="mb-4">
        <CardContent className="p-4 flex items-center gap-2">
          <ImageIcon className="h-5 w-5 text-primary-500" />
          <div>
            <h2 className="text-xl font-bold">Banner translations</h2>
            <p className="text-xs text-ink-dim">
              Every frame the designers have pushed for translation. Open one to read its copy and
              approve the wording before any language is generated from it.
            </p>
          </div>
        </CardContent>
      </Card>

      {error && (
        <Alert variant="error" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12"><Spinner size="lg" /></div>
          ) : banners.length === 0 ? (
            <p className="text-sm text-ink-dim text-center py-12">
              Nothing pushed yet. A banner appears here the first time a designer sends its frame
              from the Figma plugin.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Banner</TableHead>
                  <TableHead className="w-56">File</TableHead>
                  <TableHead className="w-20">Strings</TableHead>
                  <TableHead className="w-24">Waiting</TableHead>
                  <TableHead className="w-64">Languages</TableHead>
                  <TableHead className="w-40">Last push</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {banners.map((banner) => {
                  const waiting = waitingOn(banner);
                  // The row is the link to the banner, so it must work from the keyboard too —
                  // a reviewer who tabs through the list opens with Enter or Space like any link.
                  const open = () => navigate(`/figma-review/${banner.id}`);
                  const onKeyDown = (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      open();
                    }
                  };
                  return (
                    <TableRow
                      key={banner.id}
                      className="cursor-pointer focus-visible:bg-surface-2/50 focus-visible:outline-none"
                      role="link"
                      tabIndex={0}
                      aria-label={`Open banner ${banner.frame_name}`}
                      onClick={open}
                      onKeyDown={onKeyDown}
                    >
                      <TableCell className="font-bold text-ink">{banner.frame_name}</TableCell>
                      <TableCell className="text-ink-dim truncate">{banner.file_name}</TableCell>
                      <TableCell className="text-ink-dim">{banner.string_count}</TableCell>
                      <TableCell>
                        {waiting > 0
                          ? <Badge variant="warning">{waiting}</Badge>
                          : <span className="text-ink-mute">—</span>}
                      </TableCell>
                      <TableCell>
                        {(banner.languages || []).length === 0 ? (
                          <span className="text-ink-mute">not translated yet</span>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {banner.languages.map((entry) => (
                              <LanguageChip key={entry.language} entry={entry} />
                            ))}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-ink-dim">{fmtDate(banner.last_seen_at)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default FigmaReviewPage;
